import {
  Maximize,
  Minimize,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAiSidebarMode } from '../../actions/aiActions';
import AnimatedTitle from './AnimatedTitle';
import ChatHistoryPanel from './ChatHistoryPanel';
import ChatWindow from './ChatWindow';
import IconTooltipWrapper from './IconTooltipWrapper';

const COMPACT_WIDTH = '500px';
const HISTORY_SIDEBAR_WIDTH = '230px';

const styles = {
  sidebarContainer: {
    backgroundColor: '#293042',
    width: COMPACT_WIDTH,
    minWidth: COMPACT_WIDTH,
    height: '100vh',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    transition: 'min-width 0.35s ease-in-out, width 0.35s ease-in-out',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    flexShrink: 0,
  },
  headerGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  title: {
    color: '#dfdfdf',
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: '0',
  },
  iconButton: {
    background: 'none',
    border: 'none',
    color: '#dfdfdf',
    cursor: 'pointer',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    overflow: 'hidden',
    flexGrow: 1,
    minHeight: 0,
  },
  historyPanel: {
    backgroundColor: '#1e232f',
    transition: 'width 0.3s ease-out, min-width 0.3s ease-out',
    overflow: 'hidden',
    height: '100%',
    color: '#dfdfdf',
    padding: '1rem 0',
    display: 'flex',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  chatArea: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '0 1rem 1rem 1rem',
    color: '#dfdfdf',
    minHeight: 0,
  },
  messageList: {
    flexGrow: 1,
    overflowY: 'auto',
    color: '#dfdfdf',
  },
};

const AiSidebar = () => {
  const dispatch = useDispatch();
  const aiSidebarMode = useSelector(state => state.aiReducer.aiSidebarMode);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const getSidebarWidth = () => {
    switch (aiSidebarMode) {
      case 'compact':
        return COMPACT_WIDTH;
      case 'fullscreen':
        return '100%';
      case 'closed':
      default:
        return '0px';
    }
  };

  const getHistorySidebarWidth = () => {
    if (aiSidebarMode === 'fullscreen') {
      // You could return a different width for fullscreen mode here if you wanted
      // e.g., return '250px';
      return HISTORY_SIDEBAR_WIDTH;
    }
    return HISTORY_SIDEBAR_WIDTH;
  };

  const dynamicSidebarStyle = {
    ...styles.sidebarContainer,
    width: getSidebarWidth(),
    minWidth: getSidebarWidth(),
  };

  const historyPanelStyle = {
    ...styles.historyPanel,
    width: isHistoryOpen ? getHistorySidebarWidth() : '0px',
    minWidth: isHistoryOpen ? getHistorySidebarWidth() : '0px',
    padding: isHistoryOpen ? '.5rem .5rem .5rem 1rem' : '1rem 0', // Hide padding when closed
  };

  const handleMaximizeToggle = () => {
    const newMode = aiSidebarMode === 'fullscreen' ? 'compact' : 'fullscreen';
    dispatch(setAiSidebarMode(newMode));
  };
  const handleClose = () => {
    dispatch(setAiSidebarMode('closed'));
  };

  return (
    <nav style={dynamicSidebarStyle}>
      <div style={styles.header}>
        <div style={styles.headerGroup}>
          <IconTooltipWrapper
            label={
              isHistoryOpen ? 'Close Question History' : 'Open Question History'
            }>
            <button
              type='button'
              onClick={() => setIsHistoryOpen(!isHistoryOpen)}
              style={styles.iconButton}>
              {isHistoryOpen ? (
                <PanelLeftClose size={20} />
              ) : (
                <PanelLeftOpen size={20} />
              )}
            </button>
          </IconTooltipWrapper>
          <h2 style={styles.title}>WOMMI</h2>
          {/* <IconTooltipWrapper label='New Question'>
            <button
              type='button'
              onClick={() => alert('New Question Clicked!')}
              style={styles.iconButton}>
              <Plus size={20} />
            </button>
          </IconTooltipWrapper> */}
          {/* <AnimatedTitle
            // phrases={['N.O.V.A', 'NYC Ordinance Virtual Assistant']}
            phrases={[
              'W.O.M.M.I',
              'Web Operative Mangaging Municipal Intelligence',
            ]}
            // phrases={['Z.O.R.A', 'Zoning Ordinance Research Assistant']}
            style={styles.title}
          /> */}
        </div>
        <div style={styles.headerGroup}>
          {aiSidebarMode !== 'closed' && (
            <IconTooltipWrapper
              label={aiSidebarMode === 'fullscreen' ? 'Minimize' : 'Maximize'}>
              <button
                type='button'
                onClick={handleMaximizeToggle}
                style={styles.iconButton}>
                {aiSidebarMode === 'fullscreen' ? (
                  <Minimize size={20} />
                ) : (
                  <Maximize size={20} />
                )}
              </button>
            </IconTooltipWrapper>
          )}
          <IconTooltipWrapper label='Close'>
            <button
              type='button'
              onClick={handleClose}
              style={styles.iconButton}>
              <X size={20} />
            </button>
          </IconTooltipWrapper>
        </div>
      </div>
      <div style={styles.contentWrapper}>
        <div style={historyPanelStyle} aria-label='Chat history'>
          {/* Query History items will go here */}
          <ChatHistoryPanel />
        </div>
        <div style={styles.chatArea} aria-label='Chat conversation'>
          {/* Main Chat Dashboard components will go here */}
          <ChatWindow />
        </div>
      </div>
    </nav>
  );
};

export default AiSidebar;
